// A KPI target file attached to the main chat (v3 `useOrpheusFileAttachment.ts`, KUR-5/KUR-8):
// staged on the scorecards door, which answers a `file_ref` the next chat turn carries. The ref
// lives an hour and only for this user and workspace.
import { getCurrentScope, onScopeDispose, ref, type Ref } from 'vue'

import type { MessageAttachment } from '../models/orpheusSetup.types'

import { useAccount, useWorkspace } from '@/features/account'
import { isStageableFile, stageScorecardFile, STAGEABLE_FILE_ACCEPT } from '@/features/scorecards'
import { ApiError } from '@/shared/http'

export const ACCEPTED_ATTACHMENT = STAGEABLE_FILE_ACCEPT

export interface OrpheusAttachment {
  name: string
  status: 'uploading' | 'ready' | 'error'
  fileRef: string
  rowCount: number | null
  /** The backend's own sentence when it sent one; else `errorKey` names ours. */
  error: string
  errorKey: string
}

/** What a sent turn carries: the ref for the request, the name and rows for the user's bubble. */
export interface ReadyAttachment extends MessageAttachment {
  fileRef: string
}

/**
 * What a file sent with no typed words says. Read by the model as the turn's message, so it stays
 * the sentence v3 sent, whatever the locale.
 */
export function kpiFileMessage(name: string): string {
  return `Here is my KPI target file: ${name}`
}

export function attachmentIcon(name: string): 'file-excel' | 'file-csv' {
  return name.toLowerCase().endsWith('.xlsx') ? 'file-excel' : 'file-csv'
}

export interface UseOrpheusFileAttachment {
  attachment: Ref<OrpheusAttachment | null>
  attach: (file: File) => Promise<void>
  clear: () => void
  /** The ready attachment, or null — the one a send may take. */
  ready: () => ReadyAttachment | null
}

export function useOrpheusFileAttachment(): UseOrpheusFileAttachment {
  const { accountId } = useAccount()
  const { workspaceId } = useWorkspace()
  const attachment = ref<OrpheusAttachment | null>(null)
  // A later attach or a clear wins over an upload still in flight.
  let uploadId = 0
  let controller: AbortController | null = null

  function abort(): void {
    controller?.abort()
    controller = null
  }
  if (getCurrentScope()) onScopeDispose(abort)

  async function attach(file: File): Promise<void> {
    uploadId += 1
    abort()
    const base: OrpheusAttachment = {
      name: file.name,
      status: 'uploading',
      fileRef: '',
      rowCount: null,
      error: '',
      errorKey: '',
    }
    if (!isStageableFile(file)) {
      attachment.value = { ...base, status: 'error', errorKey: 'orpheus.attachment.unsupported' }
      return
    }
    const account = accountId.value
    const workspace = Number(workspaceId.value)
    if (account === undefined || !Number.isFinite(workspace)) {
      attachment.value = { ...base, status: 'error', errorKey: 'orpheus.attachment.failed' }
      return
    }

    const id = uploadId
    attachment.value = base
    controller = new AbortController()
    try {
      const staged = await stageScorecardFile(account, workspace, file, controller.signal)
      if (id !== uploadId) return
      attachment.value = { ...base, status: 'ready', ...staged }
    } catch (cause) {
      if (id !== uploadId) return
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (error.kind === 'canceled') return
      attachment.value = {
        ...base,
        status: 'error',
        error: error.userVisible && error.status !== null ? error.detail : '',
        errorKey: 'orpheus.attachment.failed',
      }
    }
  }

  function clear(): void {
    uploadId += 1
    abort()
    attachment.value = null
  }

  function ready(): ReadyAttachment | null {
    const current = attachment.value
    return current?.status === 'ready'
      ? { name: current.name, rowCount: current.rowCount, fileRef: current.fileRef }
      : null
  }

  return { attachment, attach, clear, ready }
}
